/**
 * Resolve the logo home route for the current product / client context.
 * Priority: current product homepage → client default product → applist fallback.
 */
export function resolveLogoHomeRoute({ routePath, applist = [], client = null, constants }) {
  const routePaths = constants?.ROUTE_PATHS || {};
  const path = routePath || '';

  const currentApp = (applist || []).find((app) => {
    const link = (app?.link || '').replace(/\/$/, '');
    return link && path.startsWith(link);
  });
  if (currentApp?.link) {
    return currentApp.link;
  }

  if (client?.hasVarianceAnalysisAccess) {
    return client.varianceAnalysisHomepage || routePaths.VA_ACTUAL_ANALYSIS || '/';
  }
  if (client?.hasBotAccess) {
    return client.botHomepage || routePaths.BOT_PROJECT || '/bot/project';
  }
  if (client?.hasFluxAccess) {
    return client.fluxHomepage || routePaths.FLUX_PROJECT || '/flux/project';
  }
  if (client?.hasFinancialCloseAccess) {
    return client.financialCloseHomepage || '/financialclose/home';
  }

  return applist?.[0]?.link || routePaths.ROOT || '/';
}

/** Single initial per Figma avatar guidance (avoids overflow in 32px circle). */
export function getUserInitials(user = {}) {
  const name = (user.name || '').trim();
  if (!name) return 'U';
  return name.charAt(0).toUpperCase();
}

/**
 * Whether a named slot has renderable content (Vue 2: v-slot / #foo uses $scopedSlots only).
 * @param {import('vue').default | { $slots: object, $scopedSlots: object }} vm
 * @param {string} slotName
 */
export function hasNamedSlotContent(vm, slotName) {
  const staticNodes = vm.$slots?.[slotName];
  if (Array.isArray(staticNodes) && staticNodes.some(isRenderableSlotNode)) {
    return true;
  }

  const scoped = vm.$scopedSlots?.[slotName];
  if (typeof scoped !== 'function') {
    return false;
  }

  try {
    const rendered = scoped({});
    const nodes = Array.isArray(rendered) ? rendered : [rendered];
    return nodes.some(isRenderableSlotNode);
  } catch (e) {
    return false;
  }
}

function isRenderableSlotNode(node) {
  if (node == null || node === false) return false;
  if (typeof node === 'string') return node.trim().length > 0;
  if (node.isComment) return false;
  return true;
}

/** Information Request email / requestee portal routes (no full auth client list). */
export function isPbcRequesteeRoute(path = '') {
  return String(path || '').includes('/pbc/requestee/');
}

/**
 * Whether the current user should see Admin Console in the app switcher.
 * Matches FC manager checks used on tasklist / horizon / cycle views.
 */
export function isAppSwitcherManagerUser(store) {
  const userId = store?.state?.user?.id || store?.state?.user?._id;
  if (!userId) return false;

  const userIdStr = String(userId);

  if ((store?.state?.tasklistUserType || '').toLowerCase() === 'manager') {
    return true;
  }

  const activeManagerList = store?.state?.m_financialclose?.tasks?.taskListData?.metadata?.managerlist;
  if (Array.isArray(activeManagerList) && activeManagerList.some((id) => String(id) === userIdStr)) {
    return true;
  }

  const cycles = store?.state?.m_financialclose?.cyclesForSwitcher || [];
  return cycles.some(
    (cycle) => Array.isArray(cycle?.managerlist)
      && cycle.managerlist.some((id) => String(id) === userIdStr),
  );
}

export function isAppActive(app, routePath, selectedAppKey = null) {
  if (!app) return false;
  if (selectedAppKey && selectedAppKey === app.name) return true;
  try {
    const link = (app.link || '').replace(/\/$/, '');
    const path = (routePath || '').replace(/\/$/, '');
    if (!link || !path) return false;
    if (path === link || path.startsWith(`${link}/`)) return true;
    const linkParts = link.split('/').filter(Boolean);
    const productRoot = linkParts.length ? `/${linkParts[0]}` : '';
    if (productRoot && (path === productRoot || path.startsWith(`${productRoot}/`))) {
      return true;
    }
    return false;
  } catch (e) {
    return false;
  }
}
